<template>
  <div
    class="flex items-center"
    :class="{ 'flex-col gap-2': layout == 'vertical' }"
  >
    <Avatar
      v-if="icon"
      :size="iconSize"
      :icon="icon"
      :color="color"
      no-border
    />
    <div
      class="flex flex-col"
      :class="{ 'ml-4': layout == 'horizontal', 'items-center': layout == 'vertical' }"
    >
      <div
        class="flex items-center"
        :class="{ 'justify-center': layout == 'vertical' }"
      >
        <slot name="statBefore"></slot>
        <span class="text-xl font-medium leading-5">{{ modelValue == null ? '-' : modelValue }}</span>
        <slot name="statAfter"></slot>
        <Chip
          v-if="showDifference && layout == 'horizontal' && percentageChange.change != null"
          v-tippy="{ content: differenceTooltip }"
          label
          size="sm"
          plain
          class="ml-2 -mt-1"
          :icon="percentageChange.icon"
          :severity="percentageChange.severity"
        >
          {{ percentageChange.change }}
        </Chip>
      </div>
      <span class="text-sm text-secondary">{{ label }}</span>
      <Chip
        v-if="showDifference && layout == 'vertical' && percentageChange.change != null"
        v-tippy="{ content: differenceTooltip }"
        label
        size="sm"
        plain
        class="ml-2 mt-1"
        :icon="percentageChange.icon"
        :severity="percentageChange.severity"
      >
        {{ percentageChange.change }}
      </Chip>
    </div>
  </div>
</template>

<script setup>
// vue
import { computed } from 'vue'

// components
import Chip from '@/components/chip/Chip.vue'
import Avatar from '@/components/avatar/Avatar.vue'

// utilities
import { toRoundedPercent } from '@/utilities'

const props = defineProps({
  modelValue: {
    type: Number,
    required: true,
    default: null
  },
  lastPeriodValue: {
    type: Number,
    required: false,
    default: null
  },
  label: {
    type: String,
    required: false,
    default: null
  },
  icon: {
    type: String,
    required: false,
    default: null
  },
  iconPosition: {
    type: String,
    required: false,
    default: 'left'
  },
  iconSize: {
    type: String,
    required: false,
    default: 'md'
  },
  color: {
    type: String,
    required: false,
    default: 'secondary'
  },
  layout: {
    type: String,
    required: false,
    default: 'horizontal',
    validator: value => ['horizontal', 'vertical'].includes(value)
  },
  differenceTooltip: {
    type: String,
    required: false,
    default: null
  },
  showDifference: {
    type: Boolean,
    required: false,
    default: true
  }
})
const percentageChange = computed(() => {
  let numericChange = 0
  let percentValue = 0
  let wasCalculated = false

  // Calculate percentage change only if lastPeriodValue is not zero to avoid division by zero
  if (props.lastPeriodValue != null && props.lastPeriodValue !== 0) {
    percentValue = (props.modelValue - props.lastPeriodValue) / props.lastPeriodValue
    // Get numeric value for comparison (returns number, not string)
    numericChange = toRoundedPercent(percentValue, true)
    wasCalculated = true
  }

  let severity
  let icon

  // Compare numeric value (not string) to determine severity
  if (numericChange < 0) {
    severity = 'danger'
    icon = 'arrow-down'
  } else if (numericChange > 0) {
    severity = 'success'
    icon = 'arrow-up'
  } else {
    severity = 'secondary'
    icon = 'minus'
  }

  // Format change as string for display (returns string with % symbol)
  // Return '0%' if calculation was performed and result is 0%, null if not calculated
  const displayChange = wasCalculated ? toRoundedPercent(percentValue) : null

  return { change: displayChange, severity, icon }
})
</script>

<style></style>
